import {makeStyles} from '@material-ui/core';
import {alpha} from '@material-ui/core/styles';
import {getPanelChangeBadgeBackground} from './panelChangeGuides';

/** Shared list / row styles for the change-log panel (Summary + All changes tabs). */
export const usePanelChangeListStyles = makeStyles(theme => ({
  list: {
    flex: 1,
    minHeight: 120,
    overflowY: 'auto',
    padding: theme.spacing(0.5, 1.5, 1.5),
  },
  listFill: {
    flex: 1,
    minHeight: 0,
    maxHeight: 'none',
    flexShrink: 1,
    overflowY: 'auto',
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  groupHeaderFirst: {
    marginTop: 0,
  },
  groupHeaderCompact: {
    marginTop: 0,
  },
  summaryGroup: {
    marginBottom: theme.spacing(0.75),
    padding: theme.spacing(0.75, 1, 0.75, 0.875),
    borderRadius: 4,
    backgroundColor:
      theme.palette.type === 'dark'
        ? alpha(theme.palette.common.white, 0.04)
        : alpha(theme.palette.common.black, 0.03),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryGroupHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginTop: 0,
    marginBottom: theme.spacing(0.25),
  },
  summaryGroupTitle: {
    flex: 1,
    minWidth: 0,
  },
  summaryGroupTitlePrimary: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  changeLogTitlePrimary: {
    fontWeight: 700,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  summaryGroupTitleSecondary: {
    display: 'block',
    marginTop: 2,
    fontWeight: 700,
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  scopeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    minHeight: 18,
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    flexShrink: 0,
    backgroundColor: getPanelChangeBadgeBackground(theme),
  },
  scopeBadgeInteractive: {
    cursor: 'pointer',
  },
  scopeBadgeExpanded: {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
  },
  scopeBadgeButton: {
    flexShrink: 0,
    borderRadius: 4,
  },
  scopeExpandedList: {
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.25, 0),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.65)}`,
    maxHeight: 160,
    overflowY: 'auto',
    '& $groupHeader': {
      marginTop: 0,
    },
  },
  scopeExpandedHint: {
    display: 'block',
    padding: theme.spacing(0, 0.75, 0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.625rem',
    fontStyle: 'italic',
  },
  summaryGroupRow: {
    marginBottom: 0,
    paddingBottom: 0,
  },
  summaryTransitionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  summaryTransitionValues: {
    flex: 1,
    minWidth: 0,
  },
  adUnitButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flex: 1,
    minWidth: 0,
    justifyContent: 'flex-start',
    padding: theme.spacing(0.25, 0.75),
    marginLeft: theme.spacing(-0.75),
    borderRadius: 4,
    color: theme.palette.primary.main,
    transition: 'background-color 120ms ease',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? 'rgba(255,255,255,0.08)'
          : theme.palette.action.hover,
    },
  },
  adUnitName: {
    fontWeight: 700,
    color: 'inherit',
  },
  adUnitNameStatic: {
    fontWeight: 700,
    flex: 1,
    minWidth: 0,
  },
  groupCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 6px',
    borderRadius: 4,
    fontSize: '0.65rem',
    fontWeight: 700,
    lineHeight: 1,
    color: theme.palette.text.primary,
    flexShrink: 0,
    backgroundColor: getPanelChangeBadgeBackground(theme),
  },
  row: {
    paddingLeft: theme.spacing(1),
    borderLeft: `2px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '&:last-child': {
      marginBottom: 0,
      paddingBottom: 0,
    },
  },
  rowFlush: {
    paddingLeft: 0,
    borderLeft: 'none',
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
  },
  attName: {
    fontWeight: 600,
    marginTop: 2,
  },
  values: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  valuesFlush: {
    marginTop: 0,
  },
  oldValue: {
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
    wordBreak: 'break-word',
  },
  newValue: {
    color: theme.palette.success.main,
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  disabledValue: {
    color: theme.palette.error.main,
    fontWeight: 600,
    wordBreak: 'break-word',
  },
  arrow: {
    fontSize: 16,
    color: theme.palette.text.disabled,
  },
  time: {
    display: 'block',
    color: theme.palette.text.disabled,
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  rowFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: 2,
    minWidth: 0,
  },
  rowFooterActorOnly: {
    justifyContent: 'flex-start',
  },
  actorGroupWrap: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  actorGroupRoot: {
    display: 'inline-flex',
  },
  // `&&` doubles class specificity so these beat MuiAvatar-root (40px default)
  actorGroupAvatar: {
    '&&': {
      width: 24,
      height: 24,
      fontSize: '0.75rem',
      border: 'none',
      marginLeft: -4,
    },
  },
  changeActor: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: 1,
  },
  changeActorAvatar: {
    '&&': {
      width: 24,
      height: 24,
      fontSize: '0.75rem',
      flexShrink: 0,
    },
  },
  changeActorName: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    lineHeight: 1.2,
    minWidth: 0,
  },
  actorFilterOption: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  actorFilterAvatar: {
    '&&': {
      width: 24,
      height: 24,
      fontSize: '0.75rem',
      flexShrink: 0,
    },
  },
  actorFilterAvatarCompact: {
    '&&': {
      width: 24,
      height: 24,
      fontSize: '0.75rem',
      flexShrink: 0,
    },
  },
  actorFilterEveryoneIcon: {
    width: 24,
    height: 24,
    fontSize: '1.5rem',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  actorFilterEveryoneIconCompact: {
    width: 24,
    height: 24,
    fontSize: '1.1rem',
    color: 'inherit',
    flexShrink: 0,
  },
  actorFilterCompactLabel: {
    maxWidth: 72,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.6875rem',
    fontWeight: 600,
  },
  scopeLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  tagIcon: {
    fontSize: '0.875rem',
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  sspIcon: {
    height: 14,
    width: 14,
    flexShrink: 0,
    position: 'relative',
    top: 3,
  },
  empty: {
    padding: theme.spacing(1, 0),
  },
}));
